/** iOS Apple card -> same-WebView CSRF POST. No redirects, storage, client
 * nonce generation, raw decoding or email/name identity shortcuts. The UI must
 * also require backend nativeApple availability before offering this path.
 */
import { apiPost } from "@/api/client";

import type { NativeAuthPurpose } from "./nativeAuth";
import type { ProviderAuthResult } from "./walletAuth";

type Invoke = (
  command: string,
  args?: Record<string, unknown>,
) => Promise<unknown>;
type NativeWindow = Window & { __TAURI_INTERNALS__?: { invoke?: Invoke } };
type Challenge = { challengeId: string; nonceHash: string; expiresIn: number };
const ORIGINS = new Set([
  "https://app.tiltracing.com",
  "https://hrw.monkeytilt.pro",
]);
let inFlight = false;
let generation = 0;

function invoke(): Invoke | undefined {
  if (typeof window === "undefined" || !ORIGINS.has(window.location.origin))
    return undefined;
  return (window as NativeWindow).__TAURI_INTERNALS__?.invoke;
}

export async function nativeAppleAvailable(): Promise<boolean> {
  const ipc = invoke();
  if (!ipc) return false;
  try {
    const result = (await ipc("native_auth_capabilities")) as {
      version?: number;
      apple?: boolean;
    } | null;
    return result?.version === 1 && result.apple === true;
  } catch {
    return false;
  }
}

export async function cancelNativeAppleAuth(): Promise<void> {
  generation += 1;
  try {
    await invoke()?.("cancel_native_apple_signin");
  } catch {
    /* old shells fail closed */
  }
}

export async function authenticateNativeApple(
  purpose: NativeAuthPurpose = "login",
): Promise<ProviderAuthResult> {
  if (inFlight) throw new Error("native_auth_in_progress");
  if (purpose !== "login" && purpose !== "connect")
    throw new Error("native_auth_invalid");
  inFlight = true;
  const attempt = ++generation;
  const checkCancelled = () => {
    if (attempt !== generation) throw new Error("user_cancelled");
  };
  // Only this stack-local reference holds the token. Neither the caller nor
  // SessionProvider receives it, even on an API or bridge error.
  let identityToken: unknown;
  try {
    if (!(await nativeAppleAvailable()))
      throw new Error("apple_native_unavailable");
    checkCancelled();
    const challenge = await apiPost<Challenge>("/session/apple/challenge", {
      purpose,
    });
    checkCancelled();
    if (
      !challenge ||
      typeof challenge.challengeId !== "string" ||
      !/^[A-Za-z0-9_-]{43}$/.test(challenge.challengeId) ||
      typeof challenge.nonceHash !== "string" ||
      !/^[0-9a-f]{64}$/.test(challenge.nonceHash) ||
      !Number.isInteger(challenge.expiresIn) ||
      challenge.expiresIn <= 0 ||
      challenge.expiresIn > 180
    )
      throw new Error("native_apple_challenge_invalid");
    const ipc = invoke();
    if (!ipc) throw new Error("apple_native_unavailable");
    try {
      identityToken = await ipc("start_native_apple_signin", {
        nonceHash: challenge.nonceHash,
      });
    } catch (error) {
      // Never leak arbitrary native error payloads (which may include token
      // material on an incompatible shell) into the page's error reporting.
      if (
        error === "user_cancelled" ||
        (error instanceof Error && error.message === "user_cancelled")
      ) {
        // eslint-disable-next-line preserve-caught-error -- native errors may contain identity tokens
        throw new Error("user_cancelled");
      }
      // eslint-disable-next-line preserve-caught-error -- never retain a potentially secret-bearing native cause
      throw new Error("apple_native_unavailable");
    }
    checkCancelled();
    if (
      typeof identityToken !== "string" ||
      identityToken.length < 20 ||
      identityToken.length > 16384 ||
      !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(identityToken)
    ) {
      throw new Error("native_apple_token_invalid");
    }
    const result = await apiPost<{
      authenticated: boolean;
      requiresSignIn?: boolean;
      pendingLink?: boolean;
    }>("/session/apple/authenticate", {
      purpose,
      challengeId: challenge.challengeId,
      identityToken,
    });
    checkCancelled();
    if (result?.authenticated === true) return { status: "authenticated" };
    if (
      result?.authenticated === false &&
      result.requiresSignIn === true &&
      result.pendingLink === true
    ) {
      return { status: "unlinked" };
    }
    throw new Error("native_apple_response_invalid");
  } finally {
    inFlight = false;
  }
}
